import { useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { Modal } from "@veorest/ui";
import type { PartnerOrderRow } from "../../lib/partnerOrders";
import { useI18n, type TranslationKey } from "../../i18n";

export type DeclineReasonKey = "busy" | "far" | "closed" | "other";

const REASON_KEYS: DeclineReasonKey[] = ["busy", "far", "closed", "other"];

const REASON_LABEL_KEYS: Record<DeclineReasonKey, TranslationKey> = {
  busy: "orders.declineModal.reasons.busy",
  far: "orders.declineModal.reasons.far",
  closed: "orders.declineModal.reasons.closed",
  other: "orders.declineModal.reasons.other",
};

interface DeclineOrderModalProps {
  open: boolean;
  order: PartnerOrderRow | null;
  busy?: boolean;
  onClose: () => void;
  /** Receives the composed reason string (label — optional note). */
  onConfirm: (reason: string) => Promise<void> | void;
}

/**
 * In-app decline flow — replaces the browser confirm/prompt pair so the
 * workspace keeps its native application feel.
 */
export function DeclineOrderModal({
  open,
  order,
  busy,
  onClose,
  onConfirm,
}: DeclineOrderModalProps) {
  const { t } = useI18n();
  const [reasonKey, setReasonKey] = useState<DeclineReasonKey>("busy");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) return;
    setReasonKey("busy");
    setNote("");
  }, [open, order?.id]);

  if (!order) return null;

  const handleConfirm = () => {
    const reason = [t(REASON_LABEL_KEYS[reasonKey]), note.trim()]
      .filter(Boolean)
      .join(" — ");
    void onConfirm(reason);
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!busy) onClose();
      }}
      panelClassName="max-w-md"
    >
      <div className="flex items-start justify-between gap-3 border-b border-[var(--border-subtle)] px-5 py-4">
        <div className="flex items-start gap-3">
          <span className="decline-modal__icon" aria-hidden>
            <AlertTriangle className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h2 className="text-lg font-extrabold text-[var(--text-primary)]">
              {t("orders.declineModal.title")}
            </h2>
            <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
              {t("orders.declineModal.subtitle")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[var(--border-subtle)]"
          onClick={onClose}
          disabled={busy}
          aria-label={t("common.close")}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-3 px-5 py-4">
        <p className="decline-modal__order">
          <strong>{order.displayNumber}</strong>
          <span aria-hidden> · </span>
          <span className="truncate">{order.customer_name}</span>
          <span aria-hidden> · </span>
          <span className="truncate text-[var(--text-muted)]">
            {order.tenant_name}
          </span>
        </p>

        <div className="space-y-1.5">
          <p className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
            {t("orders.declineModal.reasonLabel")}
          </p>
          <div className="flex flex-wrap gap-2">
            {REASON_KEYS.map((key) => {
              const active = reasonKey === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={`decline-modal__reason ${
                    active ? "decline-modal__reason--active" : ""
                  }`}
                  aria-pressed={active}
                  disabled={busy}
                  onClick={() => setReasonKey(key)}
                >
                  {t(REASON_LABEL_KEYS[key])}
                </button>
              );
            })}
          </div>
        </div>

        <label className="block space-y-1.5">
          <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
            {t("orders.declineModal.noteLabel")}
          </span>
          <textarea
            className="input-base min-h-[72px] w-full resize-none"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={t("orders.declineModal.notePlaceholder")}
            disabled={busy}
          />
        </label>
      </div>

      <div className="flex gap-2 border-t border-[var(--border-subtle)] px-5 py-4">
        <button
          type="button"
          className="btn-secondary min-h-[44px] flex-1"
          onClick={onClose}
          disabled={busy}
        >
          {t("common.cancel")}
        </button>
        <button
          type="button"
          className="btn-primary min-h-[44px] flex-1"
          style={{ background: "#dc2626" }}
          onClick={handleConfirm}
          disabled={busy}
        >
          {t("orders.declineModal.confirm")}
        </button>
      </div>
    </Modal>
  );
}
